import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ZoneRow, ZoneFcRow } from 'models/ShipBobPerformanceModel';
import FcFilterChips from './FcFilterChips';

function money(value: number | null): string {
  return value == null ? '—' : `$${value.toFixed(2)}`;
}

function pct(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(1)}%`;
}

function days(value: number | null): string {
  return value == null ? '—' : value.toFixed(2);
}

const zoneOrder = (zone: string) =>
  zone === 'Intl / Other' ? 99 : Number(zone.replace('Zone ', ''));

export default function ZonesTable({
  zones,
  zoneByFc,
}: {
  zones: ZoneRow[];
  zoneByFc?: ZoneFcRow[];
}) {
  const [selectedFc, setSelectedFc] = useState<string>('All');

  const fcNames = useMemo(() => {
    const counts = new Map<string, number>();
    (zoneByFc || []).forEach(r => {
      counts.set(r.fc, (counts.get(r.fc) || 0) + r.shipments);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([fc]) => fc);
  }, [zoneByFc]);

  // FC selected → that FC's zone mix, with share recomputed within the FC.
  const rows: ZoneRow[] = useMemo(() => {
    if (selectedFc === 'All') return zones;
    const fcRows = (zoneByFc || []).filter(r => r.fc === selectedFc);
    const fcTotal = fcRows.reduce((s, r) => s + r.shipments, 0) || 1;
    return fcRows
      .map(r => ({ ...r, sharePct: (r.shipments / fcTotal) * 100 }))
      .sort((a, b) => zoneOrder(a.zone) - zoneOrder(b.zone));
  }, [zones, zoneByFc, selectedFc]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Shipping zones
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Carrier zone per shipment (1 = closest to the warehouse, 8 =
          cross-country; international &amp; special services grouped as Intl /
          Other). Cost and transit time rise with zone — a heavier mix of high
          zones means inventory is far from customers. Select a fulfillment
          center to see its zone mix.
        </Typography>
      </Box>

      <FcFilterChips
        fcNames={fcNames}
        selected={selectedFc}
        onSelect={setSelectedFc}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        {selectedFc !== 'All' && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Zone mix — {selectedFc}
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Zone</TableCell>
                <TableCell align="right">Shipments</TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      selectedFc === 'All'
                        ? 'Share of all shipments in this range'
                        : `Share of ${selectedFc}'s shipments`
                    }
                  >
                    <span>Share</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Avg cost</TableCell>
                <TableCell align="right">
                  <Tooltip title="Average calendar days from ship to delivery (delivered shipments only).">
                    <span>Transit (d)</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Delivered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(z => (
                <TableRow key={z.zone} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{z.zone}</TableCell>
                  <TableCell align="right">
                    {z.shipments.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">{pct(z.sharePct)}</TableCell>
                  <TableCell align="right">{money(z.avgCost)}</TableCell>
                  <TableCell align="right">{days(z.avgTransitDays)}</TableCell>
                  <TableCell align="right">{pct(z.deliveredPct)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: '#94A3B8' }}>
                    No data for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
